import { useMemo } from 'react';
import _ from 'lodash';
import { HelpOutline as HelpOutlineIcon } from '@material-ui/icons';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useHelpUrl } from '../../../utilities/HelpUtilities';
import usePanelDataService from '../../../services/PanelDataService';
import { agentFilterId, dateTimeFilterId, testFilterId, timeRangeFilterId } from '../../filters';
import {
    LATENCY_FORMATTER_CONFIG,
    getTableData,
    hopFormatter,
    locationFormatter,
    pathRowIssue,
    filterPath,
} from './PathDetailPanelUtilities';
import { useArrayLoader } from '../../data-table/Loaders';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { LegacyIcon } from '../../icon';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { DEFAULT_INVALID_VALUE, FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import useIsPrinting, { getPrintRowOverride } from '../../../utilities/PrintUtilities';
import DataTable from '../../data-table/DataTable';

const dataSource = {
    url: '/ngp/v2/service-tests/path',
    params: {
        properties: 'path,graph',
        rowLimit: 1,
    },
};

const columns = [
    {
        title: t`Hop`,
        width: 80,
        mapping: 'hopNumber',
        align: 'right',
        formatter: hopFormatter,
    },
    {
        type: COLUMN_TYPES.ICON,
        icons: [
            {
                getIcon: (row) => (row.split ? <LegacyIcon title="Split Route" icon="call_split" /> : null),
            },
        ],
        width: 30,
        uiOnly: true,
    },
    {
        title: t`Host Name`,
        minWidth: 175,
        width: 425,
        maxWidth: 425,
        mapping: 'hostname',
    },
    {
        type: COLUMN_TYPES.ICON,
        icons: [
            {
                getIcon: (row) => <PathIssueIcon row={row} />,
            },
        ],
        width: 30,
        uiOnly: true,
    },
    {
        title: t`IP Address`,
        width: 325,
        mapping: 'ipAddress',
    },
    {
        type: COLUMN_TYPES.NUMERIC,
        csvTitle: t`Path Delay`,
        id: `Path Delay-avgResponseTime`,
        assocId: `Path Delay-responseHistogram`,
        sortable: false,
        formatter: _.merge({ type: FORMATTER_TYPES.INTEGER }, LATENCY_FORMATTER_CONFIG),
        width: 105,
        mapping: 'avgResponseTime',
    },
    {
        type: COLUMN_TYPES.BARCHART,
        title: t`Path Delay`,
        id: `Path Delay-responseHistogram`,
        metricColor: 'pathLogLatency',
        width: 100,
        mapping: 'responseHistogram',
    },
    {
        title: t`ISP`,
        minWidth: 75,
        mapping: 'provider',
    },
    {
        title: t`Location`,
        minWidth: 150,
        width: 300,
        maxWidth: 765,
        mapping: 'chosenLocation',
        formatter: (value) => locationFormatter(value, { emptyValue: DEFAULT_INVALID_VALUE }),
    },
    {
        title: t`Latitude`,
        minWidth: 75,
        maxWidth: 125,
        mapping: 'chosenLocation.latitude',
    },
    {
        title: t`Longitude`,
        minWidth: 75,
        maxWidth: 125,
        mapping: 'chosenLocation.longitude',
    },
    {
        title: t`ASN`,
        width: 75,
        mapping: 'asn',
    },
];

function PathDetailPanel(props) {
    const data = usePanelDataService(dataSource);
    const [tableData] = useMemo(() => {
        const pathData = data?.data?.[0];
        return [getTableData(pathData)];
    }, [data]);
    const loader = useArrayLoader(tableData, filterPath);
    const isPrinting = useIsPrinting();
    const rowOverride = getPrintRowOverride();

    const defaultPrintVisibleRows = tableData?.length || 150;

    if (!data) return null;

    return (
        <div className="flex-container">
            {!isPrinting || (isPrinting && rowOverride) ? (
                <ReportingDataTable panelProps={props} columns={columns} loader={loader} visibleRows={0} />
            ) : (
                <DataTable panelProps={props} columns={columns} loader={loader} visibleRows={defaultPrintVisibleRows} />
            )}
        </div>
    );
}

function PathIssueIcon(props) {
    const { row } = props;
    const pathIssueHelpUrl = useHelpUrl('PathIssues');
    const issue = pathRowIssue(row);

    if (!issue) return null;

    return (
        <a href={pathIssueHelpUrl} target="_" title={issue}>
            <HelpOutlineIcon />
        </a>
    );
}

PathDetailPanel.propTypes = panelPropTypes;

PathDetailPanel.defaultProps = {
    ...panelPropDefaults,
    autoHeight: true,
};

export default Panel({
    filters: {
        required: [timeRangeFilterId, dateTimeFilterId, testFilterId, agentFilterId],
        optional: [],
    },
})(PathDetailPanel);
